import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormText,
} from "reactstrap";
import Select from "react-select";
import Pagination from "../../components/pagination/pagination";
import TableHelper from "../../components/tableHelper";
import TaxRatesTable from "../../components/taxRatesTable";
import { getCookie } from "../../jwt/_helpers/cookie";
import { taxRatesService } from "../../jwt/_services/taxRates.service";
import { useFetch } from "../../utils/fetchHook";

const taxCategories = [
  { value: "vat_gst", label: "VAT / GST" },
  { value: "wht", label: "Withholding Tax (WHT)" },
  { value: "other", label: "Other" },
];

const calculationMethods = [
  { value: "inclusive", label: "Inclusive" },
  { value: "exclusive", label: "Exclusive" },
];

const categoryHelpText = {
  vat_gst: "Typically used for value-added or goods and services taxes charged on an invoice.",
  wht: "Typically used for withholding taxes that are applied according to local tax regulations.",
  other: "Used for any other tax, levy, fee, or adjustment that does not fall under VAT/GST or withholding tax.",
};

const TaxRates = ({ subuserInvoicesEnabled }) => {
  const token = getCookie("token");
  const [forceUpdate, setForce] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [ordering, setOrdering] = useState({ order: "-", name: "id", sort: "id" });

  const [taxCategory, setTaxCategory] = useState(taxCategories[0]);
  const [calculationMethod, setCalculationMethod] = useState(calculationMethods[0]);
  const [legalNoteText, setLegalNoteText] = useState("");

  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [newTaxName, setNewTaxName] = useState("");
  const [newTaxRate, setNewTaxRate] = useState("");
  const [editingTaxRate, setEditingTaxRate] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [apiData, fetchError, loading] = useFetch(
    "GET",
    `tax-rates/?ordering=${ordering.order + ordering.name}&page=${currrentPage}&page_size=${pageSize}&search=${query}`,
    token,
    false,
    forceUpdate
  );

  useEffect(() => {
    setTableData(apiData?.results || []);
    setDataCount(apiData?.count || 0);
  }, [apiData]);

  const openAddModal = () => {
    setError("");
    setSuccess(false);
    setEditingTaxRate(null);
    setNewTaxName("");
    setNewTaxRate("");
    setTaxCategory(taxCategories[0]);
    setCalculationMethod(calculationMethods[0]);
    setLegalNoteText("");
    setTaxModalOpen(true);
  };

  const openEditModal = (taxRate) => {
    setError("");
    setSuccess(false);

    setEditingTaxRate(taxRate);
    setNewTaxName(taxRate?.name ?? "");
    setNewTaxRate(
      taxRate?.rate === 0 || taxRate?.rate ? String(taxRate.rate) : ""
    );

    const nextCategory =
      taxCategories.find((c) => c.value === taxRate?.category) ||
      taxCategories[0];
    setTaxCategory(nextCategory);

    const nextCalculationMethod =
      calculationMethods.find((m) => m.value === taxRate?.calculation_method) ||
      calculationMethods[0];
    setCalculationMethod(nextCalculationMethod);

    setLegalNoteText(taxRate?.legal_text ?? "");
    setTaxModalOpen(true);
  };

  const handleSaveTaxRate = async () => {
    setError("");
    setSuccess(false);

    if (!newTaxName.trim()) {
      setError("Tax name is required.");
      return;
    }

    const parsedRate = parseFloat(newTaxRate);
    if (Number.isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100) {
      setError("Tax rate must be between 0 and 100.");
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name: newTaxName.trim(),
        rate: parsedRate,
        category: taxCategory.value,
        calculation_method: calculationMethod.value,
        legal_text: legalNoteText.trim() || null,
      };

      if (editingTaxRate?.id) {
        await taxRatesService.update(editingTaxRate.id, payload, token);
        setSuccessMsg("Tax rate updated.");
      } else {
        await taxRatesService.create(payload, token);
        setSuccessMsg("Tax rate added.");
      }

      setSuccess(true);
      setTaxModalOpen(false);
      setForce(prev => prev + 1);
    } catch {
      setError("Unable to save tax rate.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="d-flex justify-content-between">
          <h5 className="mb-0">Tax Rates</h5>
          <Button
            color="primary"
            outline
            onClick={openAddModal}
            disabled={loading || isSaving || !subuserInvoicesEnabled}
          >
            Add Tax Rate
          </Button>
        </CardHeader>
        <CardBody>
          {loading ? <TableHelper loading /> : fetchError ? <TableHelper error /> : (
            <>
              <TaxRatesTable
                tableData={tableData}
                disabled={!subuserInvoicesEnabled}
                onEdit={openEditModal}
              />
              <Pagination
                pagination={{
                  pageSize,
                  setPageSize,
                  dataCount,
                  setCurrentPage,
                  currrentPage,
                }}
              />
            </>
          )}
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{successMsg}</Alert>}
        </CardBody>
      </Card>

      <Modal isOpen={taxModalOpen} toggle={() => setTaxModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setTaxModalOpen(false)}>
          {editingTaxRate ? "Edit Tax Rate" : "Add Tax Rate"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Tax name</Label>
            <Input value={newTaxName} onChange={e => setNewTaxName(e.target.value)} />
          </FormGroup>

          <FormGroup>
            <Label>Tax rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={newTaxRate}
              onChange={e => setNewTaxRate(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Category</Label>
            <Select value={taxCategory} options={taxCategories} onChange={setTaxCategory} />
            <FormText>{categoryHelpText[taxCategory.value]}</FormText>
          </FormGroup>

          <FormGroup>
            <Label>Calculation Method</Label>
            <Select
              value={calculationMethod}
              options={calculationMethods}
              onChange={setCalculationMethod}
            />
            <FormText>
              Inclusive means the amount is already part of the invoice. Exclusive means it is added to the invoice total.
            </FormText>
          </FormGroup>

          <FormGroup>
            <Label>Legal note shown on invoice</Label>
            <Input
              type="textarea"
              rows="3"
              value={legalNoteText}
              onChange={e => setLegalNoteText(e.target.value)}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setTaxModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveTaxRate} disabled={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TaxRates;
